import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';

import type { Technology } from '../../../../domain/technology';
import { arraysAreEqual } from '../../../../services/array';

import {
  StyledFilterContainer,
  StyledTechCategoriesDropdown,
  StyledTechCategoriesDropdownButton,
  StyledTechCategoriesDropdownTooltip,
} from './styles';
import { TechCategoryDropdownOption } from './TechCategoryDropdownOption';
import { useTechCategoriesFilter } from './useTechCategoriesFilter';

export const TechCategoriesFilter = ({
  isDisabled,
  technologies,
  appliedCategories,
}: {
  isDisabled?: boolean;
  technologies: Technology[] | undefined;
  appliedCategories: string[];
}) => {
  const {
    isOpened,
    handleOpenDropdown,
    handleCloseDropdown,
    selectedCategories,
    searchText,
    handleSearchText,
    filteredOptions,
    handleClickOnIcon,
    handleTechCategoriesSelect,
    handleApplySelectedTechCategories,
  } = useTechCategoriesFilter({ technologies, appliedCategories });
  const { t } = useTranslation();

  const getDropdownButtonLabel = () => {
    if (isDisabled) {
      return t('company.technologies.filter.all');
    }
    if (appliedCategories.length === 1) {
      return appliedCategories[0];
    }
    if (appliedCategories.length > 1) {
      return `${appliedCategories[0]} + ${appliedCategories.length - 1}`;
    }
    return t('company.technologies.filter.all');
  };

  return (
    <StyledFilterContainer data-automation="TechnologiesFilter">
      <StyledTechCategoriesDropdownTooltip
        enabled={appliedCategories.length > 1}
        content={appliedCategories.join(', ')}
        minWidth={337}
      >
        <StyledTechCategoriesDropdownButton
          onClick={handleOpenDropdown}
          isSelected={selectedCategories.length > 0}
          isActive={isOpened}
          disabled={isDisabled}
          data-automation="TechnologiesFilterDdButton"
        >
          {getDropdownButtonLabel()}
          <div onClick={handleClickOnIcon}>
            <SWReactIcons
              iconName={appliedCategories.length > 0 ? 'clear-circle' : isOpened ? 'chev-up' : 'chev-down'}
              size={appliedCategories.length > 0 ? 'sm' : 'xs'}
            />
          </div>
        </StyledTechCategoriesDropdownButton>
      </StyledTechCategoriesDropdownTooltip>
      <StyledTechCategoriesDropdown
        isOpened={isOpened}
        onClose={handleCloseDropdown}
        onCloseFinish={() => handleSearchText('')}
        searchProps={{
          placeholder: t('company.technologies.filter.search') || '',
          value: searchText,
          onChange: (e) => handleSearchText(e.target.value),
        }}
        data-automation="TechnologiesFilterDd"
        maxHeight={284}
        isFooterButtonEnabled={!arraysAreEqual(selectedCategories, appliedCategories)}
        onFooterButtonClick={handleApplySelectedTechCategories}
      >
        {filteredOptions.map((option) => (
          <TechCategoryDropdownOption
            key={option.isChild ? `${option.name}_child` : `${option.name}_parent`}
            option={option}
            selectedCategories={selectedCategories}
            onTechnologiesSelect={handleTechCategoriesSelect}
          />
        ))}
      </StyledTechCategoriesDropdown>
    </StyledFilterContainer>
  );
};
